




import BillingService from '@/api/BillingService';
import { ModalOptionsV2 } from '@/components/Modal/ModalOptions';
import flagKeys from '@/constants/flagKeys';
import {
  EBillingStatusTypes,
  EBusinessUserType
} from '@/store/modules/businessTypes';
import { getBillingIsHidden, getBillingIsPending } from '@/utils/billing';
import eventBus from '@/utils/eventBus';
import { Vue, Component } from 'vue-property-decorator';

@Component({
  components: {}
})
export default class BillingModel extends Vue {
  billingService = new BillingService();

  loadingBillingUpgrade = false;

  get billingStatus() {
    const business =
      this.$store.getters['business/getBusinessInfo'].business || {};

    return business.subscriptionStatus;
  }

  get isBillingModuleV4() {
    return this.$store.getters['flags/getFlags'].includes(
      flagKeys.billingModuleV4
    );
  }

  // Gates the "Coin Network Feature" badge on every feature empty state
  // (TWISM-19008). The page-title variant of the tag is a different component and
  // guards itself — see componentsV2/atoms/BillingNetworkTag.
  get showCoinNetworkTag() {
    if (getBillingIsHidden()) return false;

    if (
      !this.$store.getters['flags/getFlags'].includes(flagKeys.billingModuleV2)
    )
      return false;

    return this.billingStatus !== EBillingStatusTypes.UNAVAILABLE;
  }

  // Every activation gate, upgrade banner and "Upgrade &" label in the legacy app
  // reads this getter, so hiding billing here switches all of them off at once
  // instead of screen by screen (TWISM-19008). The surfaces that read it inverted
  // (`!inactiveBilling`) are the non-billing ones — the widget and rewards banners,
  // the provider empty state — and they come back on, which is the point.
  get inactiveBilling() {
    if (getBillingIsHidden()) return false;

    if (
      !this.$store.getters['flags/getFlags'].includes(flagKeys.billingModuleV2)
    )
      return false;

    return (
      this.billingStatus === EBillingStatusTypes.UNSUBSCRIBED ||
      getBillingIsPending()
    );
  }

  async onClickUpgrade({
    onSuccess,
    props
  }: {
    onSuccess?: () => void;
    props?: {
      welcomeCtaText: string;
      origin: string;
      action?: 'Acceptance' | 'Reactivation';
    };
  }) {
    // Widest funnel into the acceptance and reactivation modals — ten call sites
    // — so one guard here closes all of them at once: with billing hidden there
    // is no plan to accept and nothing to reactivate (TWISM-19007). It is NOT the
    // only way in, so a new surface still needs its own guard: BannerReactiveBilling
    // emits `openSubModal` itself (guarded by its own `v-if`, TWISM-19008) and the
    // /coin-network-plan view renders the acceptance modal inline (guarded by the
    // route validator, TWISM-19010). It sits above the owner check because a
    // non-owner would otherwise get "you don't have permission to upgrade your
    // account" for a flow that no longer exists for anyone.
    if (getBillingIsHidden()) return;

    if (
      this.$store.getters['business/getManagerRole'] !== EBusinessUserType.OWNER
    ) {
      this.$notification(
        "You don't have permission to upgrade your account",
        'error'
      );
      return;
    }

    const modal =
      (props && props.action) ||
      (this.billingStatus === EBillingStatusTypes.UNAVAILABLE ||
        getBillingIsPending())
        ? 'Acceptance'
        : 'Reactivation';

    try {
      this.loadingBillingUpgrade = true;

      const {
        overview,
        features
      } = await this.billingService.getOverviewAndActivedFeatures(
        modal !== 'Reactivation'
      );

      eventBus.$emit(
        'openSubModal',
        new ModalOptionsV2({
          componentToRender: `coinNetwork${modal}Modal`,
          hideCancelButton: false,
          icon: '',
          mobileFullScreen: true
        }),
        {
          subscription: overview,
          features,
          onSuccess: onSuccess,
          ...props
        }
      );
    } finally {
      this.loadingBillingUpgrade = false;
    }
  }
}
